// Escritura offline-first generica (Paso 6). Reutilizable por todas las
// pantallas de alta de eventos (comment, weight, treatment, pregnancyCheck,
// birth, service).
//
// Hace: optimistic update en el cache de la lista (insertar arriba con
// _pendingSync) + enqueue en la outbox (Dexie) + drain best-effort. Si el
// encolado falla, rollback del optimistic.
//
// NO usa createMutation de TanStack a proposito: sus mutations usan networkMode
// "online" por default y se PAUSAN offline (no corren mutationFn hasta
// reconectar), justo lo contrario de lo que queremos. Una funcion directa es
// deterministica y siempre encola.
//
// Regla offline: una operacion = una entidad de UN animal con su clientUuid.
// Para altas multi-animal, llamar una vez por animal con `drain:false` y drenar
// una sola vez al final (un unico POST /api/sync/batch con todas las ops).
import {
    enqueue,
    getByClientUuid,
    updatePayload,
    discard,
    getIdMap,
    getDependientesDe,
} from "./outboxApi";
import { queryClient } from "$lib/query/queryClient";
import { drainOutbox } from "$lib/sync/drainer";
import { loadStorageEstablecimiento } from "$lib/java/establecimientos/establecimientostorage";

// El establecimiento de una mutación es SIEMPRE el activo al momento de crearla
// (no se puede operar sobre un establecimiento que no es el activo). Se infiere
// acá y se graba en la outbox para que el drainer la sincronice con el token
// scoped correcto (multi-establecimiento). Evita tocar cada pantalla.
function currentEstablishmentId() {
    return loadStorageEstablecimiento()?.id ?? null;
}

// Una queryKey solo sirve si está COMPLETA (`["lots", 47]`). Las pantallas la arman
// como `queryKeys.X(cab.id)` y `cab.id` se resuelve async — por eso sus queries
// llevan `enabled: !!cab.id` —, así que durante ese instante la clave vale
// `["lots", null]`. Escribir ahí manda la fila optimista a una lista que ninguna
// pantalla muestra (el usuario no ve lo que acaba de crear) y, peor, guardar esa
// clave en la outbox hace que el drainer reconcilie contra la clave equivocada.
//
// Ante una clave incompleta preferimos NO tocar el cache: la operación se encola
// igual (no se pierde nada) y la lista se acomoda en el próximo refetch. Es una
// ventana chica pero real, y el patrón está replicado en ~95 llamadas del front.
// Detectado el 2026-08-11 instrumentando el alta offline; ver docs/capacitor-plan.md §9.7.
function queryKeyUsable(queryKey) {
    if (queryKey === undefined || queryKey === null) return false;
    const completa =
        Array.isArray(queryKey) &&
        queryKey.length > 0 &&
        queryKey.every((parte) => parte !== null && parte !== undefined);
    if (!completa) {
        console.warn(
            "[outbox] queryKey incompleta, se omite el optimistic update:",
            JSON.stringify(queryKey),
        );
    }
    return completa;
}

// { entity, payload, queryKey, optimisticRow, drain = true }
export async function saveOffline({
    entity,
    payload,
    queryKey: queryKeyRecibida,
    optimisticRow,
    clientUuid: providedUuid,
    drain = true,
}) {
    // Clave incompleta (`["lots", null]`) → se trata como si no hubiera clave.
    const queryKey = queryKeyUsable(queryKeyRecibida) ? queryKeyRecibida : undefined;
    // Generamos el clientUuid aca (no dentro de enqueue) para taggear la fila
    // optimista del cache con el MISMO uuid. Asi, si despues el usuario descarta
    // la operacion (Paso 8), podemos ubicar y remover su fila optimista por
    // _clientUuid en vez de dejarla colgada con _pendingSync para siempre.
    // El caller puede PROVEERLO (saveAnimalOffline) para que el id optimista sea
    // `pending-<clientUuid>` y el drainer pueda resolver las FKs de sus hijos
    // (cadena de dependencias offline: crear observación/pesaje/etc. para un
    // animal recién creado sin conexión → ver resolvePendingRefs en drainer.js).
    const clientUuid = providedUuid ?? crypto.randomUUID();
    const previous = queryKey ? queryClient.getQueryData(queryKey) : undefined;
    if (queryKey && optimisticRow) {
        queryClient.setQueryData(queryKey, (old = []) => [
            { ...optimisticRow, _pendingSync: true, _clientUuid: clientUuid },
            ...old,
        ]);
    }
    try {
        await enqueue(entity, "CREATE", payload, { clientUuid, queryKey, establishmentId: currentEstablishmentId() });
        if (drain) drainOutbox().catch(() => {});
        return clientUuid;
    } catch (err) {
        if (queryKey && optimisticRow) queryClient.setQueryData(queryKey, previous);
        throw err;
    }
}

// Un id "real" del server es un entero positivo. Defensa contra mandar al batch un
// resourceId no numérico (rompería la deserialización Long → 400 → tumba el lote).
function isRealServerId(id) {
    return id != null && /^\d+$/.test(String(id));
}

// Compara ids SIN importar el tipo. Las filas del cache traen `id` numérico, pero
// las pantallas de detalle sacan el id de `$page.params.slug`, que es un STRING.
// Con `===` estricto, "123" !== 123 y el resultado era el peor posible: el guard
// no encontraba la fila (así que dejaba editar) y el optimistic tampoco (así que
// el cambio no se veía hasta recargar). Comparar en forma laxa acá arregla todas
// las pantallas de una vez, sin depender de que cada una convierta el tipo.
function mismoId(a, b) {
    return a != null && b != null && String(a) === String(b);
}

/** La fila de la lista cacheada que corresponde a `resourceId`, o null. */
function filaDelCache(queryKey, resourceId) {
    if (!queryKey) return null;
    const list = queryClient.getQueryData(queryKey);
    if (!Array.isArray(list)) return null;
    return list.find((r) => mismoId(r.id, resourceId)) ?? null;
}

// ¿La fila objetivo fue CREADA offline y todavía no sincronizó? Las marcamos al
// crearlas con `_clientUuid` (saveOffline) + `_pendingSync`. OJO: una fila REAL
// editada offline tiene `_pendingSync` pero NO `_clientUuid` → esa es una edición
// normal. Esto es robusto sin importar el formato del id optimista.
function isOfflineCreatedRow(fila) {
    return !!(fila && fila._pendingSync && fila._clientUuid);
}

function ensureEditable(resourceId, fila, action) {
    if (!isRealServerId(resourceId) || isOfflineCreatedRow(fila)) {
        throw new PendingNotEditableError(action);
    }
}

/**
 * Aplica una edición o un borrado sobre el CREATE que todavía está en la outbox,
 * en vez de encolar un UPDATE/DELETE.
 *
 * Es lo que permite corregir o descartar algo recién creado sin conexión: como el
 * server todavía no conoce ese registro, no hay `resourceId` real que mandar (y
 * mandar el id optimista tumbaba todo el batch con 400 — por eso antes se
 * bloqueaba). Colapsar sobre el CREATE evita el problema de raíz: al reconectar
 * sale una sola operación, ya con el valor final, o ninguna si se descartó.
 *
 * Devuelve `true` si pudo. Devuelve `false` cuando la operación ya salió o está
 * saliendo (`in_flight`), o cuando ya sincronizó y desapareció de la outbox: ahí
 * el registro pasa a existir en el server y hay que ir por el camino normal.
 */
async function colapsarSobreCreate(clientUuid, accion, payload) {
    const op = await getByClientUuid(clientUuid);
    if (!op || op.operation !== "CREATE") return false;
    // in_flight = el drainer ya la mandó y espera respuesta. Tocarla ahora es una
    // carrera: el server podría estar creándola con el payload viejo.
    if (op.status === "in_flight") return false;

    if (accion === "delete") {
        // Primero los hijos: si el padre desaparece y alguno queda, ese hijo ya no
        // tiene forma de resolver su FK.
        await descartarDependientes(clientUuid);
        await discard(op.id);
        return true;
    }
    // Merge, no reemplazo: el payload de una edición puede traer sólo los campos
    // que cambiaron. El clientUuid se preserva porque es la clave de idempotency.
    await updatePayload(op.id, { ...op.payload, ...payload, clientUuid: op.clientUuid });
    return true;
}

/** Saca de TODAS las listas cacheadas la fila optimista de un clientUuid. */
function quitarFilaOptimista(clientUuid) {
    // Barre el cache entero en vez de usar el `queryKey` que guardó la op, porque
    // una misma fila puede vivir en más de una lista: el alta de animal siembra
    // ["animals", estId] y además ["animals", estId, "all"], y la op recuerda una
    // sola. Quedarse corto deja un fantasma que la app sigue mostrando.
    for (const query of queryClient.getQueryCache().getAll()) {
        const datos = query.state.data;
        if (!Array.isArray(datos)) continue;
        if (!datos.some((r) => r?._clientUuid === clientUuid)) continue;
        queryClient.setQueryData(
            query.queryKey,
            datos.filter((r) => r?._clientUuid !== clientUuid),
        );
    }
}

/**
 * Descarta en cascada las operaciones encoladas que dependen de un CREATE que se
 * está descartando, y borra sus filas optimistas.
 *
 * Sin esto, eliminar sin conexión algo recién creado deja huérfanos a sus hijos
 * (la observación de un animal creado offline, por ejemplo): su FK
 * "pending-<uuid>" ya no va a resolverse nunca, el drainer los difiere en
 * silencio en cada ciclo y la UI le miente al usuario diciéndole que se van a
 * sincronizar solos. Nada de eso llegó nunca al server, así que descartarlos es
 * lo único que deja el estado coherente.
 *
 * Recorre en cascada (un hijo puede ser padre de otro) con un tope por uuid ya
 * visitado, para que un ciclo de referencias no lo cuelgue.
 */
async function descartarDependientes(clientUuid) {
    const visitados = new Set([clientUuid]);
    let frontera = [clientUuid];
    const descartados = [];

    while (frontera.length > 0) {
        const hijos = await getDependientesDe(frontera);
        const nuevos = hijos.filter((h) => !visitados.has(h.clientUuid));
        if (nuevos.length === 0) break;
        for (const hijo of nuevos) {
            visitados.add(hijo.clientUuid);
            await discard(hijo.id);
            quitarFilaOptimista(hijo.clientUuid);
            descartados.push(hijo);
        }
        frontera = nuevos.map((h) => h.clientUuid);
    }
    return descartados;
}

/**
 * Si el CREATE ya sincronizó, el server le asignó un id real que el drainer
 * anotó en el idmap. Sirve para editar/borrar una fila cuyo cache todavía
 * muestra el id optimista. Devuelve null si todavía no hay id real.
 */
async function serverIdDe(clientUuid) {
    const idmap = await getIdMap();
    return idmap.get(clientUuid) ?? null;
}

// Error que las pantallas pueden reconocer para mostrar un aviso claro.
export class PendingNotEditableError extends Error {
    constructor(action) {
        super(
            `Ese registro todavía se está sincronizando. Esperá a que termine para ${action}.`,
        );
        this.name = "PendingNotEditableError";
    }
}

// EDICIÓN offline (Frente A / UPDATE). Reemplaza la fila del cache (match por id ==
// resourceId) por la versión editada de forma optimista y encola un UPDATE.
// `resourceId` = id de la fila (real del server, o el optimista "pending-<uuid>" si
// se creó offline); `optimisticRow` = la fila en la shape de la lista con los nuevos
// valores. `payload` = DTO backend con el delta.
// Una fila creada offline y todavía sin confirmar NO encola un UPDATE: se corrige
// el CREATE que sigue en la outbox (ver colapsarSobreCreate). Solo queda sin poder
// editarse mientras esa op está `in_flight`, que es una ventana de segundos.
export async function updateOffline({ entity, resourceId, payload, queryKey: queryKeyRecibida, optimisticRow }) {
    // Clave incompleta (`["lots", null]`) → se trata como si no hubiera clave.
    const queryKey = queryKeyUsable(queryKeyRecibida) ? queryKeyRecibida : undefined;
    // `idEnCache` identifica la fila en la lista y NO cambia; `resourceId` es lo
    // que viaja al batch y puede pasar a ser el id real del server. Mantenerlos
    // separados evita que el optimistic deje de encontrar la fila.
    const idEnCache = resourceId;
    const fila = filaDelCache(queryKey, idEnCache);

    // Creada sin conexión y todavía sin confirmar: se corrige el CREATE encolado
    // en lugar de encolar un UPDATE contra un id que el server no conoce.
    if (isOfflineCreatedRow(fila)) {
        const clientUuid = fila._clientUuid;
        if (await colapsarSobreCreate(clientUuid, "update", payload)) {
            if (queryKey && optimisticRow) {
                queryClient.setQueryData(queryKey, (old = []) =>
                    old.map((r) => (mismoId(r.id, idEnCache) ? { ...r, ...optimisticRow } : r)),
                );
            }
            // Sigue pendiente de CREARSE: la fila conserva _pendingSync/_clientUuid.
            return clientUuid;
        }
        // Sincronizó mientras tanto: ahora existe en el server y se puede editar
        // por su id real, aunque el cache siga mostrando el optimista.
        const serverId = await serverIdDe(clientUuid);
        if (serverId == null) throw new PendingNotEditableError("editarlo");
        resourceId = serverId;
    } else {
        ensureEditable(resourceId, fila, "editarlo");
    }

    const previous = queryKey ? queryClient.getQueryData(queryKey) : undefined;
    if (queryKey && optimisticRow) {
        queryClient.setQueryData(queryKey, (old = []) =>
            old.map((r) =>
                mismoId(r.id, idEnCache) ? { ...r, ...optimisticRow, _pendingSync: true } : r,
            ),
        );
    }
    try {
        const clientUuid = await enqueue(entity, "UPDATE", payload, { queryKey, resourceId, establishmentId: currentEstablishmentId() });
        drainOutbox().catch(() => {});
        return clientUuid;
    } catch (err) {
        if (queryKey && optimisticRow) queryClient.setQueryData(queryKey, previous);
        throw err;
    }
}

// ELIMINACIÓN offline (Frente A / DELETE). Saca la fila del cache (match por id ==
// resourceId) de forma optimista y encola un DELETE. Si la fila se creó offline y
// su CREATE sigue encolado, se descarta esa operación y no se manda nada.
export async function deleteOffline({ entity, resourceId, queryKey: queryKeyRecibida }) {
    // Clave incompleta (`["lots", null]`) → se trata como si no hubiera clave.
    const queryKey = queryKeyUsable(queryKeyRecibida) ? queryKeyRecibida : undefined;
    const idEnCache = resourceId;
    const fila = filaDelCache(queryKey, idEnCache);

    if (isOfflineCreatedRow(fila)) {
        const clientUuid = fila._clientUuid;
        if (await colapsarSobreCreate(clientUuid, "delete")) {
            if (queryKey) {
                queryClient.setQueryData(queryKey, (old = []) =>
                    Array.isArray(old) ? old.filter((r) => !mismoId(r.id, idEnCache)) : old,
                );
            }
            return clientUuid;
        }
        const serverId = await serverIdDe(clientUuid);
        if (serverId == null) throw new PendingNotEditableError("eliminarlo");
        resourceId = serverId;
    } else {
        ensureEditable(resourceId, fila, "eliminarlo");
    }

    const previous = queryKey ? queryClient.getQueryData(queryKey) : undefined;
    if (queryKey) {
        queryClient.setQueryData(queryKey, (old = []) =>
            Array.isArray(old) ? old.filter((r) => !mismoId(r.id, idEnCache)) : old,
        );
    }
    try {
        // removedRow: la fila tal cual estaba antes de sacarla del cache. El
        // drainer la necesita para restaurarla si el server rechaza el DELETE
        // (ej. sin permiso) — sin esto, revertOptimistic no tiene qué reinsertar
        // y la fila queda desaparecida aunque nunca se borró en el servidor.
        const clientUuid = await enqueue(entity, "DELETE", {}, { queryKey, resourceId, establishmentId: currentEstablishmentId(), removedRow: fila });
        drainOutbox().catch(() => {});
        return clientUuid;
    } catch (err) {
        if (queryKey) queryClient.setQueryData(queryKey, previous);
        throw err;
    }
}
